import { collection, query, where, orderBy, onSnapshot, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase-init';
import { normalizePhone, normalizeCustomerName, isPotentialDuplicate } from '../utils/customer-helpers';
import { toDate } from '../utils/date-utils';

class CustomerService {
  constructor() {
    this.customers = [];
    this.listeners = [];
    this.unsubscribe = null;
  }

  // Start real-time listener
  startListener(callback) {
    if (this.unsubscribe) {
      console.warn('CustomerService listener already active');
      return;
    }

    const q = query(
      collection(db, 'customers'),
      orderBy('name', 'asc')
    );

    this.unsubscribe = onSnapshot(q, (snapshot) => {
      this.customers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: toDate(doc.data().createdAt),
        updatedAt: toDate(doc.data().updatedAt),
      }));

      
      // Notify all listeners
      this.listeners.forEach(cb => cb(this.customers));
      if (callback) callback(this.customers);
    }, (error) => {
      console.error('❌ CustomerService listener error:', error);
    });
  }

  // Stop listener
  stopListener() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  // Subscribe to customer updates
  subscribe(callback) {
    this.listeners.push(callback);
    // Immediately call with current data
    callback(this.customers);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  // Get all customers
  getCustomers() {
    return this.customers;
  }

  // Get customer by ID
  async getCustomer(id) {
    const docRef = doc(db, 'customers', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt),
      };
    }
    
    return null;
  }

  // Search customers by name, email, or phone
  async searchCustomers(searchTerm) {
    if (!searchTerm || searchTerm.trim().length < 2) {
      return [];
    }

    const term = searchTerm.toLowerCase().trim();
    const phoneDigits = normalizePhone(searchTerm);

    // Search in memory for instant results
    const results = this.customers.filter(customer => {
      const name = (customer.name || '').toLowerCase();
      const email = (customer.email || '').toLowerCase();
      const phone = normalizePhone(customer.phone || customer.phoneNumber || '');
      const address = (customer.address || '').toLowerCase();

      return (
        name.includes(term) ||
        email.includes(term) ||
        address.includes(term) ||
        (phoneDigits && phone.includes(phoneDigits))
      );
    });

    return results.slice(0, 10); // Limit to 10 results
  }

  // Find potential duplicates for a new customer
  async findDuplicates(customerData) {
    const matches = [];

    // Search by phone
    if (customerData.phone || customerData.phoneNumber) {
      const phoneNormalized = normalizePhone(customerData.phone || customerData.phoneNumber);
      
      if (phoneNormalized) {
        const phoneMatches = this.customers.filter(c => {
          const cPhone = normalizePhone(c.phone || c.phoneNumber || '');
          return cPhone === phoneNormalized;
        });
        matches.push(...phoneMatches);
      }
    }

    // Search by email
    if (customerData.email) {
      const emailLower = customerData.email.toLowerCase().trim();
      
      const emailMatches = this.customers.filter(c => {
        const cEmail = (c.email || '').toLowerCase().trim();
        return cEmail === emailLower;
      });
      matches.push(...emailMatches);
    }

    // Search by name (fuzzy)
    if (customerData.name) {
      const nameLower = customerData.name.toLowerCase().trim();
      
      const nameMatches = this.customers.filter(c => {
        const cName = (c.name || '').toLowerCase().trim();
        return cName === nameLower;
      });
      matches.push(...nameMatches);
    }

    // Remove duplicates and return
    const uniqueMatches = Array.from(
      new Map(matches.map(c => [c.id, c])).values()
    );

    return uniqueMatches.slice(0, 5); // Return top 5 matches
  }

  // Add new customer
  async addCustomer(customerData) {
    const now = new Date();
    
    const data = {
      name: normalizeCustomerName(customerData.name || ''),
      email: (customerData.email || '').trim(),
      phone: customerData.phone || '',
      phoneNormalized: normalizePhone(customerData.phone || ''),
      address: customerData.address || '',
      notes: customerData.notes || '',
      totalJobs: 0,
      totalRepairs: 0,
      lifetimeValue: 0,
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
    };

    const docRef = await addDoc(collection(db, 'customers'), data);
    
    return docRef.id;
  }

  // Update customer
  async updateCustomer(id, updates) {
    const docRef = doc(db, 'customers', id);
    
    const data = {
      ...updates,
      updatedAt: Timestamp.fromDate(new Date())
    };

    // Normalize phone if provided
    if (updates.phone) {
      data.phoneNormalized = normalizePhone(updates.phone);
    }

    // Normalize name if provided
    if (updates.name) {
      data.name = normalizeCustomerName(updates.name);
    }

    await updateDoc(docRef, data);
  }

  // Delete customer
  async deleteCustomer(id) {
    const docRef = doc(db, 'customers', id);
    await deleteDoc(docRef);
  }

  // Update customer stats (called after job changes)
  async updateCustomerStats(customerId, stats) {
    const docRef = doc(db, 'customers', customerId);
    
    await updateDoc(docRef, {
      totalJobs: stats.totalJobs || 0,
      totalRepairs: stats.totalRepairs || 0,
      lifetimeValue: stats.lifetimeValue || 0,
      updatedAt: Timestamp.fromDate(new Date())
    });
  }
}

// Export singleton instance
export const customerService = new CustomerService();
export default customerService;